import { ReadOnlyAdminUsernames, UserSchema } from "../types/auth.type";
import type { AuthTokenPayload } from "../auth/types";
import { ADMIN_READ_ONLY_ROLE } from "../auth/legacy-roles";
import { getLegacyRolesForOrg, isPlatformReadOnly, permissionsFromPayload } from "../auth/jwt";
import { isPlatformReadOnlyFromPermissions } from "../auth/platform-presets";
import { isPlatformReadOnlyPersona } from "../auth/platform-personas";

/** Mirrors sidebar read-only detection in Layout (username or email). */
export function isReadOnlyAdminUser(
  user: UserSchema | null | undefined,
): boolean {
  const readOnly = Object.values(ReadOnlyAdminUsernames).map((x) =>
    String(x).toLowerCase(),
  );
  const username = user?.username?.toLowerCase();
  const email = user?.email?.toLowerCase();
  return (
    (!!username && readOnly.includes(username)) ||
    (!!email && readOnly.includes(email))
  );
}

/** True when JWT legacy roles include admin_read_only for any org. */
export function isReadOnlyAdminFromPayload(
  payload: AuthTokenPayload | null | undefined,
): boolean {
  if (!payload) {
    return false;
  }
  const raw = payload.permissions;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return false;
  }
  for (const roles of Object.values(raw)) {
    if (Array.isArray(roles) && roles.includes(ADMIN_READ_ONLY_ROLE)) {
      return true;
    }
  }
  return false;
}

export function isReadOnlyAdmin(
  user: UserSchema | null | undefined,
  payload: AuthTokenPayload | null | undefined,
  permissionList?: readonly string[],
): boolean {
  const perms =
    permissionList ??
    (payload ? permissionsFromPayload(payload) : []);
  return (
    isReadOnlyAdminUser(user) ||
    isReadOnlyAdminFromPayload(payload) ||
    isPlatformReadOnly(payload) ||
    isPlatformReadOnlyPersona(payload?.platform?.personas) ||
    isPlatformReadOnlyFromPermissions(perms)
  );
}

export function canMutate(
  user: UserSchema | null | undefined,
  payload: AuthTokenPayload | null | undefined,
  canAccessFn: (required: string, legacyRoles?: readonly string[]) => boolean,
  required: string,
  legacyRoles?: readonly string[],
): boolean {
  if (isReadOnlyAdmin(user, payload)) {
    return false;
  }
  return canAccessFn(required, legacyRoles);
}

import { canAccessAdminApp } from "../auth/admin-app-access";

/** @deprecated Use canAccessAdminApp — kept for call sites that pass isPlatformAdmin flag. */
export function hasAnyAdminAccess(
  payload: AuthTokenPayload | null | undefined,
  _isPlatformAdminFlag?: boolean,
): boolean {
  return canAccessAdminApp(payload);
}

export { getLegacyRolesForOrg };
